import React, { useState, useEffect } from "react";
import { Outlet, NavLink, Navigate, Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingBag, LogOut, Store, Star, Boxes, UserCircle, ShoppingCart, Menu, X, Link2, Warehouse, Upload } from "lucide-react";
import { useAdmin } from "@/context/AdminContext";
import { api } from "@/lib/api";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/inventory", label: "Inventory", icon: Warehouse },
  { to: "/admin/import", label: "Import", icon: Upload },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
  { to: "/admin/affiliates", label: "Gorilla Gateway", icon: ShoppingCart },
  { to: "/admin/product-requests", label: "Product Requests", icon: Link2, badgeKey: "requests" },
  { to: "/admin/dropshipping", label: "Other Partners", icon: Boxes },
  { to: "/admin/about", label: "About Page", icon: UserCircle },
];

export default function AdminLayout() {
  const { admin, logout } = useAdmin();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [requestCount, setRequestCount] = useState(0);

  useEffect(() => {
    if (!admin) return;
    const fetchCount = () => api.get("/admin/product-requests/count")
      .then((r) => setRequestCount(r.data.new_count || 0)).catch(() => {});
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => clearInterval(t);
  }, [admin]);

  if (admin === null) return <div className="min-h-screen flex items-center justify-center text-navy/50">Loading...</div>;
  if (admin === false) return <Navigate to="/admin/login" replace />;

  const NavItems = ({ onNavigate }) => (
    <>
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={onNavigate}
            data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-4 py-3 font-semibold transition-colors ${isActive ? "bg-brick text-white" : "text-cream/80 hover:bg-white/10"}`
            }
          >
            <n.icon className="w-5 h-5 shrink-0" /> <span className="flex-1">{n.label}</span>
            {n.badgeKey === "requests" && requestCount > 0 && (
              <span data-testid="nav-requests-badge" className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-gold text-navy text-xs font-bold flex items-center justify-center">{requestCount}</span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 space-y-1 border-t border-white/10" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
        <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-md px-4 py-3 text-cream/80 hover:bg-white/10 font-semibold">
          <Store className="w-5 h-5" /> View Store
        </a>
        <button
          data-testid="admin-logout"
          onClick={async () => { await logout(); navigate("/admin/login"); }}
          className="w-full flex items-center gap-3 rounded-md px-4 py-3 text-cream/80 hover:bg-white/10 font-semibold"
        >
          <LogOut className="w-5 h-5" /> Logout
        </button>
      </div>
    </>
  );

  return (
    <div data-testid="admin-layout" className="min-h-screen flex bg-cream">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-60 bg-navy text-cream flex-col shrink-0 sticky top-0 h-screen">
        <Link to="/admin" className="px-6 py-6 font-display text-2xl">JOHNNY<span className="text-gold">MART</span></Link>
        <NavItems />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="admin-mobile-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="relative w-[80%] max-w-[300px] bg-navy text-cream flex flex-col h-full" style={{ paddingTop: "env(safe-area-inset-top)" }}>
            <div className="flex items-center justify-between px-5 py-5">
              <Link to="/admin" onClick={() => setOpen(false)} className="font-display text-2xl">JOHNNY<span className="text-gold">MART</span></Link>
              <button data-testid="admin-drawer-close" onClick={() => setOpen(false)} className="p-2"><X className="w-6 h-6" /></button>
            </div>
            <NavItems onNavigate={() => setOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-40 bg-navy text-cream flex items-center justify-between px-4 h-14" style={{ paddingTop: "env(safe-area-inset-top)" }}>
          <button data-testid="admin-menu-toggle" onClick={() => setOpen(true)} className="p-2 -ml-2"><Menu className="w-6 h-6" /></button>
          <Link to="/admin" className="font-display text-xl">JOHNNY<span className="text-gold">MART</span></Link>
          <Link to="/" className="p-2 -mr-2"><Store className="w-5 h-5" /></Link>
        </header>

        <main className="flex-1 overflow-x-hidden">
          <div className="p-4 sm:p-6 lg:p-8 max-w-6xl w-full">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
